import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, LayoutGrid, ShieldCheck, BarChart3, Mail } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { PARTNERS, CLIENTS } from "../mock";

/* Uploaded screenshots for the feature deep-dive rows */
const IMG = {
  hero:      "https://customer-assets.emergentagent.com/job_neural-pathway-66/artifacts/47z7vnqt_01_hero_learner_dashboard.png",
  catalogue: "https://customer-assets.emergentagent.com/job_neural-pathway-66/artifacts/obhyueml_02_course_catalogue%201.png",
  paths:     "https://customer-assets.emergentagent.com/job_neural-pathway-66/artifacts/qmctuqw4_03_learning_paths.png",
  skills:    "https://customer-assets.emergentagent.com/job_neural-pathway-66/artifacts/ybpacngw_04_skills_intelligence.png",
  admin:     "https://customer-assets.emergentagent.com/job_neural-pathway-66/artifacts/l0zmm9ou_05_admin_console.png",
  attendance:"https://customer-assets.emergentagent.com/job_neural-pathway-66/artifacts/r45j5w6q_06_attendance.png",
  aiTutor:   "https://customer-assets.emergentagent.com/job_neural-pathway-66/artifacts/vucycgrr_07_incourse_ai_tutor.png",
};

const WHY_MATTERS = [
  "Learning data is scattered across tools, with no single view of who is skilled in what.",
  "No line of sight from training spend to actual business outcomes.",
  "Manual enrolments, attendance, and compliance reporting drain admin time.",
];

const WHAT_CARDS = [
  {
    icon: LayoutGrid,
    title: "Learner Experience",
    desc: "Dashboards, catalogue, learning paths, and in-course AI support.",
  },
  {
    icon: ShieldCheck,
    title: "Admin Console",
    desc: "Enrolments, cohorts, attendance, access control, and compliance reporting.",
  },
  {
    icon: BarChart3,
    title: "Skills Intelligence",
    desc: "Live workforce capability, completion, and skill-gap analytics.",
  },
];

const FEATURE_ROWS = [
  {
    id: "catalogue",
    image: IMG.catalogue,
    headline: "A catalogue learners actually navigate.",
    body: "Searchable, filterable courses by skill and technology, each with duration, ratings, and progress — so learners find the right course in seconds.",
  },
  {
    id: "paths",
    image: IMG.paths,
    headline: "Structured journeys, not scattered courses.",
    body: "Role- and certification-aligned paths sequence courses into a clear progression, with locked and unlocked stages and live path progress.",
  },
  {
    id: "skills",
    image: IMG.skills,
    headline: "See skills as data, not guesswork.",
    body: "Track active learners, completion trends, and skill-gap score by team and domain — so you know where capability is building and where it is thin.",
  },
  {
    id: "admin",
    image: IMG.admin,
    headline: "Run large programs without the admin drag.",
    body: "Approve enrolment requests, manage cohorts, control course access, and export compliance-ready reports from one console.",
  },
  {
    id: "attendance",
    image: IMG.attendance,
    headline: "Instructor-led delivery, fully tracked.",
    body: "Mark attendance across live batches, capture check-in times, flag leave, and sync records automatically.",
  },
  {
    id: "ai-tutor",
    image: IMG.aiTutor,
    headline: "A tutor inside every lesson.",
    body: "Learners get contextual explanations, code examples, and concept help from an in-course AI tutor — without leaving the module.",
  },
];

const HOW_STEPS = [
  "Onboard your workforce and map roles to skill tracks.",
  "Assign courses, labs, and cohorts.",
  "Learners progress through guided paths with AI support.",
  "Leaders track skill growth and ROI in real time.",
];

const WHO_ITEMS = [
  "L&D leaders running enterprise-wide upskilling.",
  "IT and platform teams standardising technical training.",
  "HR and compliance teams needing audit-ready records.",
];

const ScreenshotFrame = ({ src, alt }) => (
  <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-[0_20px_50px_-12px_rgba(21,52,140,0.25)]">
    <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
  </div>
);

const LXPPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 1) HERO */}
      <PageHero
        eyebrow="ENTERPRISE LMS"
        title="One platform to run learning across your entire workforce"
        subtitle="A single place to assign, track, and prove skill growth — from individual learners to cohort programs to organisation-wide skill intelligence. Built for L&D and IT leaders who need learning, delivery, and reporting in one system."
      />

      <section className="max-w-7xl mx-auto px-4 -mt-6 lg:-mt-10 pb-6">
        <Reveal>
          <ScreenshotFrame src={IMG.hero} alt="One platform to run learning across your entire workforce" />
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#15348C]/25">
                Talk to Our Expert <ArrowRight size={17} className="ml-1" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button className="bg-white text-[#15348C] border-2 border-[#15348C] hover:bg-[#15348C] hover:text-white font-semibold px-6 h-12 rounded-md">
                Book a Demo
              </Button>
            </Link>
          </div>
        </Reveal>
      </section>

      {/* 2) WHY IT MATTERS */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">WHY IT MATTERS</div>
              <h2 className="text-[#15348C] leading-tight">The gaps enterprise learning teams face today.</h2>
            </div>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {WHY_MATTERS.map((w, i) => (
              <Reveal key={w} variant="up" delay={i * 90}>
                <div className="bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F57C20]/15 flex items-center justify-center mb-4">
                    <span className="text-[#F57C20] font-bold" style={{ fontFamily: "Times New Roman, serif" }}>{i + 1}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3) WHAT IT IS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-xs font-bold tracking-widest text-[#6BBE32] mb-3">WHAT IT IS</div>
              <h2 className="text-[#15348C] leading-tight">A full-stack learning platform built on three connected layers.</h2>
              <p className="mt-4 text-gray-600 leading-relaxed">So learning, delivery, and reporting live in one system.</p>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {WHAT_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} variant="up" delay={i * 100}>
                  <div className="group bg-white border border-gray-100 rounded-2xl p-7 h-full hover:border-[#6BBE32]/40 hover:shadow-xl transition-all">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6BBE32] to-[#4F9622] flex items-center justify-center shadow-lg shadow-[#6BBE32]/30 mb-5">
                      <Icon size={26} className="text-white" />
                    </div>
                    <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors" style={{ fontSize: "1.35rem" }}>{c.title}</h3>
                    <p className="mt-3 text-gray-600 leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4) FEATURE DEEP-DIVE */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">FEATURE DEEP-DIVE</div>
              <h2 className="text-[#15348C] leading-tight">Everything L&D needs, in one workspace.</h2>
            </div>
          </Reveal>

          <div className="space-y-16 lg:space-y-24">
            {FEATURE_ROWS.map((row, idx) => {
              const reversed = idx % 2 === 1;
              return (
                <div key={row.id} className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                  <Reveal variant={reversed ? "right" : "left"} className={reversed ? "lg:order-2" : ""}>
                    <ScreenshotFrame src={row.image} alt={row.headline} />
                  </Reveal>
                  <Reveal variant={reversed ? "left" : "right"} delay={80} className={reversed ? "lg:order-1" : ""}>
                    <h3 className="text-[#15348C] leading-tight" style={{ fontSize: "1.85rem" }}>{row.headline}</h3>
                    <p className="mt-4 text-gray-600 leading-relaxed text-lg">{row.body}</p>
                  </Reveal>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5) HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-10">
              <div className="text-xs font-bold tracking-widest text-[#6BBE32] mb-3">HOW IT WORKS</div>
              <h2 className="text-[#15348C] leading-tight">From onboarding to outcomes, in four steps.</h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_STEPS.map((step, i) => (
              <Reveal key={step} variant="up" delay={i * 90}>
                <div className="relative bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <div className="absolute -top-4 left-6 w-10 h-10 rounded-full bg-[#15348C] text-white flex items-center justify-center font-bold shadow-md" style={{ fontFamily: "Times New Roman, serif" }}>
                    {i + 1}
                  </div>
                  <p className="mt-4 text-gray-700 leading-relaxed">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6) PROOF STRIP */}
      <section className="py-16 bg-[#F8F9FA] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center text-xs font-bold tracking-widest text-gray-500 mb-6">TRUSTED BY GLOBAL ENTERPRISES &amp; VENDORS</div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 opacity-80">
              {PARTNERS.slice(0, 10).map((p) =>
                p.logo ? (
                  <img key={p.name} src={p.logo} alt={p.name} className="h-8 max-w-[120px] object-contain" />
                ) : (
                  <span key={p.name} className="text-[#15348C] font-bold text-sm">{p.name}</span>
                )
              )}
            </div>
            {CLIENTS && CLIENTS.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
                {CLIENTS.slice(0, 8).map((c) =>
                  c.logo ? (
                    <img key={c.name} src={c.logo} alt={c.name} className="h-7 max-w-[110px] object-contain" />
                  ) : (
                    <span key={c.name} className="text-gray-500 font-semibold text-sm">{c.name}</span>
                  )
                )}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* 7) WHO IT'S FOR */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-10">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">WHO IT&apos;S FOR</div>
              <h2 className="text-[#15348C] leading-tight">Built for the teams that own workforce capability.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {WHO_ITEMS.map((w, i) => (
              <Reveal key={w} variant="up" delay={i * 90}>
                <div className="flex gap-4 bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <CheckCircle2 size={22} className="text-[#6BBE32] shrink-0 mt-0.5" />
                  <p className="text-gray-700 leading-relaxed">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8) CTA BAND */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#15348C] to-[#0A1F5C] px-8 py-12 lg:px-14 lg:py-16 text-white">
              <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-white leading-tight">See your workforce&apos;s skills in one place.</h2>
                </div>
                <div className="flex flex-wrap gap-4 lg:justify-end items-center">
                  <Link to="/contact">
                    <Button className="bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-7 h-12 rounded-md shadow-lg shadow-[#F57C20]/30">
                      Talk to Our Expert <ArrowRight size={17} className="ml-1" />
                    </Button>
                  </Link>
                  <a href="mailto:info@skillsgain.com" className="inline-flex items-center gap-2 text-white/90 hover:text-[#8BD835] font-semibold transition-colors" data-testid="cta-band-email">
                    <Mail size={18} /> info@skillsgain.com
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LXPPage;
